import { SearchIndexes } from '@modules-rocco/search/types/enum/search-indexes';
import type { SearchServiceAggregationParams } from '@modules-rocco/search/types/aggregations';
import type {
    FacetOptionsAggregationParams,
    SearchServiceCompositeAggregation,
} from '@modules-rocco/search/types/aggregations';
import type { FilterStates } from '@modules-rocco/filters/types/filter-states';
import { DEFAULT_SEARCH_LIST_SIZE } from '@modules-rocco/filters/config/filter-url.config';
import type { SearchableListFilterState } from '@modules-rocco/filters/filter-state/searchable-list-filter-state';
import { createSearchableListFilterState } from '@modules-rocco/filters/filter-state/searchable-list-filter-state';

/**
 * Maps FilterStates to query aggregations for SearchService.
 * Processes facet options and generates aggregation parameters based on the SearchIndex.
 *
 * @param filterStates - The current state of filters
 * @param searchIndex - The search index to use for aggregations
 * @returns SearchServiceAggregationParams or null if no valid aggregations
 */
export const mapFilterStatesToQueryAggregations = (
    filterStates: FilterStates,
    searchIndex: SearchIndexes | undefined
): SearchServiceAggregationParams | null => {
    // facet aggregations
    const aggFacetOptions =
        filterStates.facet?.facetOptions?.reduce<
            FacetOptionsAggregationParams[]
        >((acc, facetOptionState) => {
            if (facetOptionState.state.search) {
                acc.push({
                    facet: facetOptionState.id,
                    search: facetOptionState.state.search,
                });
            }

            return acc;
        }, []) ?? [];

    return getAggregationsBySearchIndex(
        searchIndex,
        filterStates,
        aggFacetOptions
    );
};

/**
 * Generates aggregation parameters based on the SearchIndex and FilterStates.
 * Returns specific aggregation configurations for Products, Projects, Professionals, and Articles.
 * @param searchIndex - The search index to use for aggregations
 * @param filterStates - The current state of filters
 * @param aggFacetOptions - Facet options for aggregations
 * @returns SearchServiceAggregationParams specific to the search index, or null if index is undefined
 */
const getAggregationsBySearchIndex = (
    searchIndex: SearchIndexes | undefined,
    filterStates: FilterStates,
    aggFacetOptions: FacetOptionsAggregationParams[]
): SearchServiceAggregationParams | null => {
    if (searchIndex === SearchIndexes.Products) {
        return {
            aggCategories: {},
            aggProductPriceRange: true,
            aggProductPriceHistogram: true,
            aggProductItemsCount: true,
            aggProductColors: true,
            aggProductMaterials: mapSearchableListToAggregationQueryParams(
                filterStates.states.materials
            ),
            aggProductLengthRange: true,
            aggProductWidthRange: true,
            aggProductHeightRange: true,
            aggProductDiameterRange: true,
            aggProductWeightRange: true,
            aggLibraryRanges: mapSearchableListToAggregationQueryParams(
                filterStates.states.libraryRanges
            ),
            aggProfessionals: mapSearchableListToAggregationQueryParams(
                filterStates.states.professionals
            ),
            aggBrands: mapSearchableListToAggregationQueryParams(
                filterStates.states.brands
            ),
            aggDesigners: mapSearchableListToAggregationQueryParams(
                filterStates.states.designers
            ),
            aggFacetOptions,
            aggDownloads: {},
            aggAwards: true,
        };
    }

    if (searchIndex === SearchIndexes.Projects) {
        return {
            aggCategories: {},
            aggProfessionals: mapSearchableListToAggregationQueryParams(
                filterStates.states.professionals
            ),
            aggProjectItemsCount: true,
            aggProfessions: mapSearchableListToAggregationQueryParams(
                filterStates.states.professions
            ),
            aggProfessionalCategories:
                mapSearchableListToAggregationQueryParams(
                    filterStates.states.professionalCategories
                ),
            aggAwards: true,
        };
    }

    if (searchIndex === SearchIndexes.Professionals) {
        return {
            aggCategories: {},
            aggProfessionalProjectCountRanges: true,
            aggBusinessFocuses: true,
            aggProfessionalItemsCount: true,
            aggAssociations: mapSearchableListToAggregationQueryParams(
                filterStates.states.associations
            ),
        };
    }

    if (searchIndex === SearchIndexes.Articles) {
        return {
            aggCategories: {},
            aggProfessionals: mapSearchableListToAggregationQueryParams(
                filterStates.states.professionals
            ),
        };
    }

    return null;
};

const mapSearchableListToAggregationQueryParams = (
    filterState: SearchableListFilterState = createSearchableListFilterState({
        items: [],
        selectedItems: [],
        search: '',
        firstCharacterGroups: {},
    })
): SearchServiceCompositeAggregation => {
    const { search = '', size = DEFAULT_SEARCH_LIST_SIZE } = filterState;

    return {
        search: search,
        size: Math.max(
            size > DEFAULT_SEARCH_LIST_SIZE ? size : DEFAULT_SEARCH_LIST_SIZE,
            filterState.items.length
        ),
    };
};
